// ActionRamp.tsx - the two-tier input ramp (locked dx5), rendered in the dock. Tier 1 is the
// multiple-choice action (Fold / Call / Raise) - an on-ramp that NEVER auto-fills a dollar amount. The
// buttons are GATED: disabled until the read is locked (the whole read-first point of Diagnose). Picking
// Raise (on a street that has a raise) opens tier 2, the manual <BetMathBuilder>. Fold/Call commit
// straight away. River has no Raise (Fold/Call only), so tier 2 never opens there. Ported from the
// mockup #dg-acts / #dg-lockhint / #dg-betmath + openBet.
import { useState } from 'react';
import type { ActionDef, BetMathDef } from './data';
import { BetMathBuilder } from './BetMathBuilder';

export interface ActionRampProps {
  actions: ActionDef[];
  /** the street's manual-raise config (null when there is no raise, e.g. river). */
  bet: BetMathDef | null;
  /** the read must be locked before any action unlocks. */
  locked: boolean;
  /** commit an action. `amount` is present only for a built raise. */
  onCommit: (key: ActionDef['key'], amount?: string) => void;
}

export function ActionRamp({ actions, bet, locked, onCommit }: ActionRampProps) {
  const [building, setBuilding] = useState(false);

  // if the read is unlocked (e.g. the user hit Revise) the builder must not linger open.
  if (building && (!locked || !bet)) setBuilding(false);

  if (building && bet) {
    return (
      <BetMathBuilder
        bet={bet}
        onCancel={() => setBuilding(false)}
        onConfirm={(amount) => onCommit('raise', amount)}
      />
    );
  }

  const handle = (a: ActionDef) => {
    if (!locked) return;
    if (a.key === 'raise' && bet) {
      setBuilding(true);
      return;
    }
    onCommit(a.key);
  };

  return (
    <>
      {!locked && <div className="lockhint">Lock your read first, then choose your action.</div>}
      <div className="acts">
        {actions.map((a) => (
          <button
            key={a.key}
            type="button"
            className={'btn ' + a.cls}
            disabled={!locked}
            onClick={() => handle(a)}
          >
            {a.label}
            <s>{a.sub}</s>
          </button>
        ))}
      </div>
    </>
  );
}
